import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Headphones, Mail, ArrowLeft, Clock, DollarSign, FileAudio, Sparkles, ExternalLink, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer-facing "My Studio Projects" dashboard.
 * Route: /my-studio-projects?email=customer@example.com
 * Lists every recording-studio project a customer has ever created.
 */
export default function MyStudioProjects() {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [inputEmail, setInputEmail] = useState(initialEmail);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const fetchProjects = async (targetEmail) => {
    if (!targetEmail) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/recording-studio/projects/client/${encodeURIComponent(targetEmail)}`);
      if (!res.ok) throw new Error('Failed to load your projects');
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (e) {
      setError(e.message || 'Something went wrong');
      setProjects([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  useEffect(() => {
    if (initialEmail) fetchProjects(initialEmail);
  }, [initialEmail]);

  const handleLookup = (e) => {
    e.preventDefault();
    setEmail(inputEmail.trim());
    fetchProjects(inputEmail.trim());
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };
  const fmtMoney = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '—');

  return (
    <div className="min-h-screen py-12 px-6" data-testid="my-studio-projects">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/recording-studio" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 text-sm" data-testid="back-to-studio">
            <ArrowLeft className="w-4 h-4" />
            Back to Recording Studio
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Headphones className="w-6 h-6 text-black" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-foreground">My Studio Projects</h1>
          </div>
          <p className="text-muted-foreground">
            Look up every recording project you&rsquo;ve booked with Casa Samba by email.
          </p>
        </div>

        {/* Email lookup form */}
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleLookup}
          className="flex flex-col sm:flex-row gap-3 mb-8 bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-4"
          data-testid="email-lookup-form"
        >
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="email"
              value={inputEmail}
              onChange={(e) => setInputEmail(e.target.value)}
              placeholder="Your email address"
              required
              className="w-full pl-12 pr-4 py-3 bg-background/70 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="lookup-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="lookup-submit"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? 'Loading…' : 'Find My Projects'}
          </button>
        </motion.form>

        {/* Error state */}
        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-sm" data-testid="lookup-error">
            {error}
          </div>
        )}

        {/* Empty state */}
        {searched && !loading && !error && projects.length === 0 && (
          <div className="text-center py-16 bg-card/40 border border-border/40 rounded-2xl" data-testid="empty-state">
            <FileAudio className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">No projects yet under {email}</h3>
            <p className="text-muted-foreground mb-6">Book your first recording session and it will appear here.</p>
            <Link
              to="/recording-studio"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90"
            >
              Start a New Project
              <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Project list */}
        {!loading && projects.length > 0 && (
          <div className="space-y-4" data-testid="projects-list">
            <p className="text-sm text-muted-foreground mb-2">
              Showing <span className="text-foreground font-semibold">{projects.length}</span> project{projects.length === 1 ? '' : 's'} for <span className="text-foreground font-semibold">{email}</span>
            </p>
            {projects.map((p) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card/60 backdrop-blur-md border border-border/50 rounded-2xl p-6 hover:border-primary/40 transition-colors"
                data-testid={`project-card-${p.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs uppercase tracking-wider font-bold text-primary">{p.tier_name || p.tier || 'Project'}</span>
                      <StatusBadge status={p.status} />
                      <StatusBadge status={p.payment_status} kind="payment" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-1">
                      {p.project_name || `Project ${p.id.slice(0, 8)}`}
                    </h3>
                    {p.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{p.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" />
                        Created {fmtDate(p.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5" />
                        {fmtMoney(p.price)}
                      </span>
                      {p.turnaround_days ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5" />
                          {p.turnaround_days}-day turnaround
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 md:items-end">
                    {p.tier === 'live_session' && (p.status === 'in_progress' || p.status === 'paid') && (
                      <Link
                        to={`/admin/studio-session?project=${p.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-400 text-black font-semibold rounded-full text-sm"
                        data-testid={`enter-studio-${p.id}`}
                      >
                        <Headphones className="w-4 h-4" />
                        Enter Studio
                      </Link>
                    )}
                    {p.payment_status !== 'paid' && (
                      <Link
                        to={`/recording-studio?project=${p.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-full text-sm text-foreground hover:bg-white/5"
                        data-testid={`resume-${p.id}`}
                      >
                        Resume Payment
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatusBadge = ({ status, kind = 'status' }) => {
  if (!status) return null;
  const palette = {
    // project status
    pending:      'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    in_progress:  'bg-blue-500/15   text-blue-400   border-blue-500/30',
    complete:     'bg-green-500/15  text-green-400  border-green-500/30',
    completed:    'bg-green-500/15  text-green-400  border-green-500/30',
    cancelled:    'bg-red-500/15    text-red-400    border-red-500/30',
    // payment status
    paid:         'bg-green-500/15  text-green-400  border-green-500/30',
    unpaid:       'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    refunded:     'bg-red-500/15    text-red-400    border-red-500/30',
  };
  const cls = palette[String(status).toLowerCase()] || 'bg-white/5 text-muted-foreground border-border';
  const label = String(status).replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {kind === 'payment' ? `pay: ${label}` : label}
    </span>
  );
};
